import React, { useEffect, useState } from 'react';
import { Question } from '../types';
import { questionService } from '../services/questionService';
import { matrixScoreService } from '../services/matrixScoreService';

interface Props {
  open: boolean;
  questionnaireId: number;
  question: Question;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * 已发布问卷"查看 / 改评分"弹窗：
 *   - SINGLE_CHOICE / MULTIPLE_CHOICE / DROPDOWN：每个选项一行 (content + score input)
 *   - MATRIX：rows × columns 网格，每个 cell 一个 score input
 *   - 其它题型（TEXT / DATE / SLIDER / DESCRIPTION / FILL_IN_BLANK / ...）：
 *     提示"该题型没有评分字段"
 *
 * 保存只调 PATCH /admin/questionnaires/{qnId}/questions/{qId}/scores
 * 不更新 title / options 内容 / settings 等其它字段。
 */
const ScoreEditModal: React.FC<Props> = ({ open, questionnaireId, question, onClose, onSaved }) => {
  const [optionScores, setOptionScores] = useState<Record<number, string>>({});
  const [matrixScores, setMatrixScores] = useState<Record<string, Record<number, string>>>({});
  const [matrixRows, setMatrixRows] = useState<string[]>([]);
  const [matrixCols, setMatrixCols] = useState<{ id: number; content: string }[]>([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const isOptionBased = question.type === 'SINGLE_CHOICE'
    || question.type === 'MULTIPLE_CHOICE'
    || question.type === 'DROPDOWN';
  const isMatrix = question.type === 'MATRIX';

  useEffect(() => {
    if (!open) return;
    setErr('');
    // option-based: 从 question.options 读已有 score
    if (isOptionBased) {
      const map: Record<number, string> = {};
      for (const o of question.options || []) {
        map[o.id] = (o.score ?? 0).toString();
      }
      setOptionScores(map);
    }
    // matrix: 拉 matrix-scores + 解析 settings 拿 rows / 用 options 拿 cols
    if (isMatrix) {
      try {
        const cfg = JSON.parse(question.settings || '{}');
        setMatrixRows(Array.isArray(cfg.rows) ? cfg.rows : []);
      } catch { setMatrixRows([]); }
      // columns = question.options（每个 option 是一列）
      setMatrixCols((question.options || []).map(o => ({ id: o.id, content: o.content })));
      // 拉现有 cell scores
      matrixScoreService.getCellScores(question.id)
        .then(cells => {
          const grid: Record<string, Record<number, string>> = {};
          for (const c of cells) {
            if (!grid[c.rowLabel]) grid[c.rowLabel] = {};
            grid[c.rowLabel][c.columnOptionId] = c.score.toString();
          }
          setMatrixScores(grid);
        })
        .catch(() => setMatrixScores({}));
    }
  }, [open, question.id, question.type, isOptionBased, isMatrix, question.options, question.settings]);

  const handleSave = async () => {
    setSaving(true);
    setErr('');
    try {
      const payload: any = {};
      if (isOptionBased) {
        const opts: Record<number, number> = {};
        for (const [k, v] of Object.entries(optionScores)) {
          const n = parseInt(v, 10);
          if (!Number.isNaN(n)) opts[Number(k)] = n;
        }
        payload.optionScores = opts;
      }
      if (isMatrix) {
        const cells: { rowLabel: string; columnOptionId: number; score: number }[] = [];
        for (const row of matrixRows) {
          for (const col of matrixCols) {
            const raw = matrixScores[row]?.[col.id];
            if (raw === undefined || raw === '') continue;
            const n = parseInt(raw, 10);
            if (Number.isNaN(n)) continue;
            cells.push({ rowLabel: row, columnOptionId: col.id, score: n });
          }
        }
        payload.matrixCellScores = cells;
      }
      await questionService.updateScores(questionnaireId, question.id, payload);
      onSaved();
      onClose();
    } catch (e: any) {
      setErr(e?.response?.data?.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[85vh] flex flex-col">
        <div className="px-6 py-4 border-b border-surface-container-high flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold font-headline text-on-surface">修改评分</h3>
            <p className="text-sm font-body text-on-surface-variant mt-0.5 line-clamp-1">{question.title}</p>
          </div>
          <button onClick={onClose} className="text-on-surface-variant hover:text-on-surface">
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {!isOptionBased && !isMatrix && (
            <div className="text-center py-8 text-on-surface-variant font-body">
              该题型（{question.typeDisplayName || question.type}）没有评分字段。
            </div>
          )}

          {isOptionBased && (
            <div className="space-y-2">
              <p className="text-xs font-label text-on-surface-variant mb-3">
                修改选项分数。保存后只更新分数，不影响题目本身或学生作答。
              </p>
              {(question.options || []).map((opt, idx) => (
                <div key={opt.id} className="flex items-center gap-3 py-2 border-b border-surface-container-high">
                  <span className="text-sm font-label text-on-surface-variant w-8">{String.fromCharCode(65 + idx)}.</span>
                  <span className="flex-1 text-sm font-body text-on-surface">{opt.content}</span>
                  <input
                    type="number"
                    value={optionScores[opt.id] ?? ''}
                    onChange={(e) => setOptionScores({ ...optionScores, [opt.id]: e.target.value })}
                    className="w-24 px-3 py-1.5 border border-outline-variant rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
                  />
                  <span className="text-sm font-label text-on-surface-variant">分</span>
                </div>
              ))}
            </div>
          )}

          {isMatrix && (
            <div>
              <p className="text-xs font-label text-on-surface-variant mb-3">
                每个单元格（行 × 列）一个分数。留空 = 不修改该单元格。
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-surface-container-low">
                      <th className="px-3 py-2 text-left font-label text-on-surface-variant">题项 \ 选项</th>
                      {matrixCols.map(c => (
                        <th key={c.id} className="px-3 py-2 text-center font-label text-on-surface-variant min-w-[80px]">
                          {c.content}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {matrixRows.map(row => (
                      <tr key={row} className="border-t border-surface-container-high">
                        <td className="px-3 py-2 font-body text-on-surface align-middle">{row}</td>
                        {matrixCols.map(col => (
                          <td key={col.id} className="px-2 py-2 text-center">
                            <input
                              type="number"
                              value={matrixScores[row]?.[col.id] ?? ''}
                              onChange={(e) => setMatrixScores({
                                ...matrixScores,
                                [row]: { ...(matrixScores[row] || {}), [col.id]: e.target.value },
                              })}
                              className="w-16 px-2 py-1 border border-outline-variant rounded-lg text-sm text-center focus:outline-none focus:ring-2 focus:ring-primary-200"
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {err && (
            <div className="mt-4 text-sm text-danger-700 bg-danger-50 border border-danger-200 rounded-lg px-3 py-2">
              {err}
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t border-surface-container-high flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-sm font-label text-on-surface-variant hover:text-on-surface"
          >
            取消
          </button>
          <button
            onClick={handleSave}
            disabled={saving || (!isOptionBased && !isMatrix)}
            className="px-4 py-2 text-sm font-label bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? '保存中...' : '保存评分'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScoreEditModal;
